'use client'

import Link from 'next/link'
import { useSidebar } from './animated-docs-layout'
import type { DocNavItem } from '../../lib/docs-nav'

interface BreadcrumbsProps {
  slug: string[]
}

function formatBreadcrumbSegment(segment: string): string {
  return segment.replace(/-/g, ' ')
}

function findByHref(items: DocNavItem[], href: string): DocNavItem | null {
  const norm = (p: string) => (p === '/' ? p : p.replace(/\/+$/, ''))
  const target = norm(href)
  for (const item of items) {
    if (norm(item.href) === target) return item
    if (item.children) {
      const found = findByHref(item.children, href)
      if (found) return found
    }
  }
  return null
}

export function DocBreadcrumbs({ slug }: BreadcrumbsProps) {
  const { toggleSidebar, navigation } = useSidebar()

  return (
    <div className="mt-16.5 mb-4 flex items-center justify-between gap-4">
      {/* Mobile menu button */}
      <button
        type="button"
        onClick={toggleSidebar}
        className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 md:hidden dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
          />
        </svg>
        <span className="sr-only">Menu</span>
      </button>

      <nav className="flex flex-1 items-center space-x-2 text-sm text-slate-600 dark:text-slate-300">
        <Link
          href="/docs/"
          className="hover:text-primary-600 dark:hover:text-primary-400"
        >
          Docs
        </Link>
        {slug.map((segment, index) => {
          const prefix = slug.slice(0, index + 1)
          const href = `/docs/${prefix.map((s) => s.toLowerCase()).join('/')}/`
          const item = findByHref(navigation, href)
          const label = item?.title ?? formatBreadcrumbSegment(segment)
          const fromTitle = Boolean(item?.title)
          const isLast = index === slug.length - 1
          // Link only real pages; wrappers are text so crumbs don't bounce to first child
          const linkable = Boolean(item?.isPage) && !isLast

          return (
            <span key={href} className="flex items-center space-x-2">
              <span>/</span>
              {isLast || !linkable ? (
                <span
                  className={
                    [
                      isLast
                        ? 'font-medium text-slate-900 dark:text-slate-100'
                        : '',
                      fromTitle ? '' : 'capitalize',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                >
                  {label}
                </span>
              ) : (
                <Link
                  href={href}
                  className={[
                    'hover:text-primary-600 dark:hover:text-primary-400',
                    fromTitle ? '' : 'capitalize',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {label}
                </Link>
              )}
            </span>
          )
        })}
      </nav>
    </div>
  )
}
